import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { backendService } from '../../services/backendService'
import {
  buildFarmPresetBundle,
  fetchFarmCustomPresets,
  fetchDoctorCustomPresets,
  getInstallerDownloadUrl,
  presetToBundleItem,
  type FarmCustomPreset,
  type DoctorCustomPreset,
  type PresetSection,
} from '../../services/companionService'

// ── Constants ─────────────────────────────────────────────────────────────────

const SECTION_LABELS: Record<PresetSection, string> = {
  machine: 'Printer',
  filament: 'Filament',
  process: 'Process',
}

const SECTION_ORDER: PresetSection[] = ['machine', 'filament', 'process']

const PRINTER_LABELS: Record<string, string> = {
  k1: 'Creality K1',
  k1max: 'Creality K1 Max',
  h2d: 'Bambu H2D',
}

// ── Component ────────────────────────────────────────────────────────────────

const API_BASE = backendService.getBaseURL()
const TOOLBOX_URL = getInstallerDownloadUrl(API_BASE, 'exe')

export const PresetsBrowser: React.FC = () => {
  const [customPresets, setCustomPresets] = useState<FarmCustomPreset[] | DoctorCustomPreset[]>([])
  const [presetsLoading, setPresetsLoading] = useState(false)
  const [presetsError, setPresetsError] = useState<string | null>(null)
  const [filterPrinter, setFilterPrinter] = useState('')
  const [filterSection, setFilterSection] = useState('')
  const [filterNozzle, setFilterNozzle] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const { userType } = useAuth()
  const isFarm = userType === 'farm'
  const isDoctor = userType === 'doctor'
  const canManagePresets = isFarm || isDoctor

  useEffect(() => {
    if (!canManagePresets) return
    let cancelled = false
    const fetchPresets = async () => {
      setPresetsLoading(true)
      setPresetsError(null)
      try {
        const authToken = backendService.getAuthToken()
        if (!authToken) throw new Error('No authentication token found')
        const presets = isFarm
          ? await fetchFarmCustomPresets(API_BASE, authToken)
          : await fetchDoctorCustomPresets(API_BASE, authToken)
        if (!cancelled) setCustomPresets(presets)
      } catch {
        if (!cancelled) setPresetsError('Could not load your presets')
      } finally {
        if (!cancelled) setPresetsLoading(false)
      }
    }
    fetchPresets()
    return () => { cancelled = true }
  }, [userType, canManagePresets, isFarm])

  // Derived: unique printers from the loaded presets
  const availablePrinters = useMemo(() => {
    const uniq = [...new Set(customPresets.map(p => p.printer))].sort()
    return uniq
  }, [customPresets])

  // Sections available for the selected printer (or all if none selected)
  const availableSections = useMemo(() => {
    const pool = filterPrinter
      ? customPresets.filter(p => p.printer === filterPrinter)
      : customPresets
    const sections = new Set(pool.map(p => p.section))
    return SECTION_ORDER.filter(s => sections.has(s))
  }, [customPresets, filterPrinter])

  // Nozzle sizes available for the selected printer (or all if none selected)
  const availableNozzles = useMemo(() => {
    const pool = filterPrinter
      ? customPresets.filter(p => p.printer === filterPrinter)
      : customPresets
    return [...new Set(pool.map(p => p.nozzle ?? ''))].filter(Boolean).sort()
  }, [customPresets, filterPrinter])

  // Reset section and nozzle filters when printer changes
  useEffect(() => { setFilterSection('') }, [filterPrinter])
  useEffect(() => { setFilterNozzle('') }, [filterPrinter])

  const filteredPresets = useMemo(() => {
    return customPresets.filter(p => {
      if (filterPrinter && p.printer !== filterPrinter) return false
      if (filterSection && p.section !== filterSection) return false
      if (filterNozzle && (p.nozzle ?? '') !== filterNozzle) return false
      return true
    })
  }, [customPresets, filterPrinter, filterSection, filterNozzle])

  // Selections persist across filter changes — the bulk bar
  // counts ALL selected presets, not just the visible ones.

  const allSelected = filteredPresets.length > 0 && filteredPresets.every(p => selectedIds.has(p.id))

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      // Deselect only the currently visible presets (keep selections in other views)
      setSelectedIds(prev => {
        const next = new Set(prev)
        filteredPresets.forEach(p => next.delete(p.id))
        return next
      })
    } else {
      // Add all visible presets to existing selection
      setSelectedIds(prev => {
        const next = new Set(prev)
        filteredPresets.forEach(p => next.add(p.id))
        return next
      })
    }
  }

  const handlePresetDownload = async (preset: FarmCustomPreset | DoctorCustomPreset) => {
    try {
      const blob = await buildFarmPresetBundle([presetToBundleItem(preset)])
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `girasol-preset-${preset.name.replace(/\s+/g, '_')}.zip`
      a.rel = 'noopener noreferrer'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      const blob = new Blob([JSON.stringify(preset.json_content, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${preset.name}.json`
      a.rel = 'noopener noreferrer'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }

  const handleBulkDownload = async () => {
    const selected = customPresets.filter(p => selectedIds.has(p.id))
    if (selected.length === 0) return
    try {
      const blob = await buildFarmPresetBundle(selected.map(presetToBundleItem))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `girasol-bundle-${selected.length}-presets.zip`
      a.rel = 'noopener noreferrer'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // fallback: download individually
      for (const p of selected) {
        const blob = new Blob([JSON.stringify(p.json_content, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${p.name}.json`
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        await new Promise(r => setTimeout(r, 300))
      }
    }
  }

  const printerLabel = (slug: string) => PRINTER_LABELS[slug] ?? slug

  const sectionLabel = (section: string) =>
    SECTION_ORDER.includes(section as PresetSection)
      ? SECTION_LABELS[section as PresetSection]
      : section

  if (!canManagePresets) {
    return (
      <div className="presets-browser">
        <h2 className="presets-browser-title">Preset Library</h2>
        <div className="presets-section-body">
          <p className="presets-farm-status">Sign in as a doctor or farm to manage your custom presets.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="presets-browser">
      <h2 className="presets-browser-title">My Presets</h2>

      {/* ── Filter bar ── */}
      <div className="presets-filter-bar">
        <div className="presets-filter-group">
          <label className="presets-filter-label" htmlFor="filter-printer">Printer</label>
          <select
            id="filter-printer"
            className="presets-filter-select"
            value={filterPrinter}
            onChange={e => setFilterPrinter(e.target.value)}
          >
            <option value="">All printers</option>
            {availablePrinters.map(p => (
              <option key={p} value={p}>{printerLabel(p)}</option>
            ))}
          </select>
        </div>

        <div className="presets-filter-group">
          <label className="presets-filter-label" htmlFor="filter-section">Type</label>
          <select
            id="filter-section"
            className="presets-filter-select"
            value={filterSection}
            onChange={e => setFilterSection(e.target.value)}
          >
            <option value="">All types</option>
            {availableSections.map(s => (
              <option key={s} value={s}>{sectionLabel(s)}</option>
            ))}
          </select>
        </div>

        <div className="presets-filter-group">
          <label className="presets-filter-label" htmlFor="filter-nozzle">Nozzle</label>
          <select
            id="filter-nozzle"
            className="presets-filter-select"
            value={filterNozzle}
            onChange={e => setFilterNozzle(e.target.value)}
          >
            <option value="">All nozzles</option>
            {availableNozzles.map(n => (
              <option key={n} value={n}>{n}mm</option>
            ))}
          </select>
        </div>

        {filteredPresets.length > 0 && (
          <div className="presets-filter-group presets-select-all-group">
            <button
              className="presets-select-all-btn"
              onClick={toggleSelectAll}
              type="button"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        )}
      </div>

      {/* ── Bulk download bar ── */}
      {selectedIds.size > 0 && (
        <div className="presets-bulk-bar">
          <span className="presets-bulk-count">{selectedIds.size} preset{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <button className="presets-bulk-download-btn" onClick={handleBulkDownload} type="button">
            Download Selected
          </button>
        </div>
      )}

      {/* ── Preset list ── */}
      <div className="presets-section-body">
        {presetsLoading && (
          <p className="presets-farm-status">Loading your presets...</p>
        )}
        {presetsError && (
          <p className="presets-farm-status presets-farm-error">{presetsError}</p>
        )}
        {!presetsLoading && !presetsError && customPresets.length === 0 && (
          <p className="presets-farm-status">No custom presets yet. Upload presets from your Toolbox.</p>
        )}
        {!presetsLoading && !presetsError && customPresets.length > 0 && filteredPresets.length === 0 && (
          <p className="presets-farm-status">No presets match the selected filters.</p>
        )}
        {filteredPresets.length > 0 && (
          <div className="presets-slicer-downloads">
            {filteredPresets.map(preset => {
              const isSelected = selectedIds.has(preset.id)
              return (
                <div key={preset.id} className={`presets-slicer-download-row ${isSelected ? 'presets-farm-row-selected' : ''}`}>
                  <label className="presets-farm-row-check">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(preset.id)}
                    />
                  </label>
                  <div className="presets-farm-row-info">
                    <span className="presets-farm-row-name">{preset.name}</span>
                    <span className="presets-farm-row-meta">
                      {preset.slicer.replace(/_/g, ' ')} &middot;{' '}
                      {sectionLabel(preset.section)}
                      {preset.nozzle ? ` \u00b7 ${preset.nozzle}mm` : ''}
                      &middot; {new Date(preset.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    className="presets-download-btn"
                    onClick={() => handlePresetDownload(preset)}
                    type="button"
                  >
                    Download
                  </button>
                </div>
              )
            })}
          </div>
        )}
        <p className="presets-import-note">
          Use the <strong>Girasoles Toolbox</strong> to upload new custom presets from your slicer.
        </p>
        <a
          href={TOOLBOX_URL}
          className="presets-toolbox-download-btn"
          target="_blank"
          rel="noopener noreferrer"
        >
          Download Girasoles Toolbox
        </a>
      </div>
    </div>
  )
}

export default PresetsBrowser
